import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config';
import { getToken } from '../services/authService';

const isImage = (filename) => {
    const ext = (filename || '').split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
};

export default function MediaPickerModal({ isOpen, onClose, onSelect }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const fetchFiles = useCallback(async () => {
        if (!isOpen) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/media`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error('Failed to fetch media');
            const data = await res.json();
            // Only images that can be embedded
            setFiles((data || []).filter((f) => isImage(f.name)));
        } catch (_) {
            setFiles([]);
        } finally {
            setLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        fetchFiles();
        setSearch('');
    }, [fetchFiles]);

    if (!isOpen) return null;

    const baseUrl = API_BASE_URL.replace(/\/api$/, '');
    const filtered = files.filter((f) =>
        !search || f.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 99999,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                style={{
                    background: '#fff', borderRadius: 12,
                    width: '90vw', maxWidth: 860, maxHeight: '85vh',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
                    background: '#f8fafc', flexShrink: 0,
                }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Media Library</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            Click an image to select it
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            fontSize: 22, color: '#64748b', lineHeight: 1, padding: 4,
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Search */}
                <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                    <input
                        type="text"
                        placeholder="Search by filename..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: '100%', padding: '8px 12px', borderRadius: 8,
                            border: '1px solid #e2e8f0', fontSize: 13, outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
                            Loading media...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>
                            {search ? 'No matching images found.' : 'No images uploaded yet.'}
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                            gap: 12,
                        }}>
                            {filtered.map((file) => {
                                const absoluteUrl = `${baseUrl}${file.path}`;
                                return (
                                    <button
                                        key={file.path}
                                        type="button"
                                        onClick={() => onSelect(absoluteUrl)}
                                        title={file.name}
                                        style={{
                                            border: '2px solid #e2e8f0',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            background: '#f8fafc',
                                            cursor: 'pointer',
                                            padding: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#3b82f6';
                                            e.currentTarget.style.transform = 'scale(1.03)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.25)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <div style={{
                                            height: 100,
                                            background: '#f1f5f9',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}>
                                            <img
                                                src={absoluteUrl}
                                                alt={file.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                loading="lazy"
                                            />
                                        </div>
                                        <div style={{
                                            padding: '6px 8px',
                                            fontSize: 11,
                                            color: '#334155',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            background: '#fff',
                                            borderTop: '1px solid #e2e8f0',
                                        }}>
                                            {file.name}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #e2e8f0',
                    background: '#f8fafc', flexShrink: 0,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                        {filtered.length} image{filtered.length !== 1 ? 's' : ''}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: '8px 20px', borderRadius: 8, border: '1px solid #cbd5e1',
                            background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#334155',
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
